/**
 * Row Actions Component (PR A10)
 * Quick action icons for user management table rows
 *
 * Performance: Memoized + relies on singleton usePermissions cache so
 * 25 rows share a single /auth/me fetch (no per-row pop-in / layout shift).
 */
import React, { memo } from 'react';
import { Pencil, UserCog, Lock, Unlock, KeyRound, Zap, ClipboardList, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { getUserFromToken } from '../../utils/adminApi';
import { getUserStatus } from '../../utils/userStatus';
import './RowActions.css';

const RowActions = ({
  user,
  onImpersonate,
  onLockUnlock,
  onResetPassword,  onAdminResetPassword,
  onEdit,
  loading = null,
  currentUserId = null
}) => {
  const navigate = useNavigate();
  const { hasPermission, loading: permsLoading } = usePermissions();

  const canEdit = hasPermission('users', 'update');
  const canImpersonate = hasPermission('users', 'impersonate');
  const canLock = hasPermission('users', 'lock');
  const canResetPassword = hasPermission('users', 'reset_password');
  const canViewAudit = hasPermission('audit', 'view');

  // P1: Admin-immediate-reset is a more sensitive action than email-based
  // reset — restrict to Admins only (defense-in-depth above the
  // backend role gate). Uses the JWT role claim exposed via localStorage.
  const me = getUserFromToken();
  const isSuperAdmin = me && me.role === 'admin';

  const isLocked = getUserStatus(user).isLocked;
  const isSelf = currentUserId && user.id === currentUserId;

  const handleAuditClick = (e) => {
    e.stopPropagation();
    navigate(`/admin/audit-logs?actorId=${user.id}`);
  };

  // Defensive skeleton: only shows on the very first paint if the
  // singleton cache hasn't been primed yet. Reserves the same width
  // as the resolved button row to prevent layout shift.
  if (permsLoading) {
    return (
      <div
        className="row-actions row-actions-skeleton"
        onClick={(e) => e.stopPropagation()}
        aria-hidden="true"
        data-testid={`row-actions-skeleton-${user.id}`}
      >
        <span className="action-btn-skeleton" />
        <span className="action-btn-skeleton" />
        <span className="action-btn-skeleton" />
        <span className="action-btn-skeleton" />
        <span className="action-btn-skeleton" />
      </div>
    );
  }

  return (
    <div className="row-actions" onClick={(e) => e.stopPropagation()}>
      {/* Edit User */}
      {canEdit && onEdit && (
        <button
          className="action-btn action-edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(user);
          }}
          data-tip="Edit user"
          aria-label="Edit user"
          data-testid={`edit-user-${user.id}`}
        >
          <Pencil className="ra-ico" />
        </button>
      )}

      {/* Impersonate */}
      {canImpersonate && !isSelf && (
        <button
          className="action-btn action-impersonate"
          onClick={(e) => {
            e.stopPropagation();
            onImpersonate(user);
          }}
          disabled={loading === `impersonate-${user.id}`}
          data-tip="Impersonate"
          aria-label="Impersonate user"
          data-testid={`impersonate-${user.id}`}
        >
          <UserCog className="ra-ico" />
        </button>
      )}

      {/* Lock/Unlock */}
      {canLock && !isSelf && (
        <button
          className={`action-btn ${isLocked ? 'action-unlock' : 'action-lock'}`}
          onClick={(e) => {
            e.stopPropagation();
            onLockUnlock(user);
          }}
          disabled={loading === `lock-${user.id}`}
          data-tip={isLocked ? 'Unlock account' : 'Lock account'}
          aria-label={isLocked ? 'Unlock this account' : 'Lock account (prevent sign-in)'}
          data-testid={`lock-toggle-${user.id}`}
        >
          {isLocked ? <Unlock className="ra-ico" /> : <Lock className="ra-ico" />}
        </button>
      )}

      {/* Reset Password */}
      {canResetPassword && !isSelf && (
        <button
          className="action-btn action-reset"
          onClick={(e) => {
            e.stopPropagation();
            onResetPassword(user);
          }}
          disabled={loading === `reset-${user.id}`}
          data-tip="Send reset email"
          aria-label="Send password reset email"
          data-testid={`reset-password-${user.id}`}
        >
          {loading === `reset-${user.id}` ? <Loader2 className="ra-ico ra-spin" /> : <KeyRound className="ra-ico" />}
        </button>
      )}

      {/* P1: Admin immediate reset (Admin only) — generates a temp
          password on the spot. Visually distinct (⚡) to avoid confusion
          with the email-based reset. */}
      {isSuperAdmin && !isSelf && onAdminResetPassword && (
        <button
          className="action-btn action-admin-reset"
          onClick={(e) => {
            e.stopPropagation();
            onAdminResetPassword(user);
          }}
          disabled={loading === `admin-reset-${user.id}`}
          data-tip="Admin reset (temporary password)"
          aria-label="Admin immediate password reset"
          data-testid={`admin-reset-password-${user.id}`}
        >
          {loading === `admin-reset-${user.id}` ? <Loader2 className="ra-ico ra-spin" /> : <Zap className="ra-ico" />}
        </button>
      )}

      {/* View Audit */}
      {canViewAudit && (
        <button
          className="action-btn action-audit"
          onClick={handleAuditClick}
          data-tip="View audit logs"
          aria-label="View audit logs"
          data-testid={`audit-logs-${user.id}`}
        >
          <ClipboardList className="ra-ico" />
        </button>
      )}

      {/* Show tooltip if no permissions */}
      {!canEdit && !canImpersonate && !canLock && !canResetPassword && !canViewAudit && (
        <span className="no-actions-text" title="You don't have permission for any actions">
          No actions available
        </span>
      )}
    </div>
  );
};

export default memo(RowActions);
